// The job's documents ledger — every file on the job, uploaded OR printed, with its
// revisions. One store: printing a submittal package (and, later, a quote) files a copy
// here on its own, so "where did that PDF go" always has an answer. Other tiles show
// their own slice of this same list; this is the whole of it.
//
// Its own file on purpose: JobPillows imports from AdminBidDetail, and the bid page
// needs this too, so living in JobPillows would make an import cycle.
import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch, API_BASE, getToken } from '../../services/apiClient';
import AttachButton from '../../components/AttachButton';

// A–Z, Other last (Ryan, Oct 22 2026: "please alphabetize this list").
const TYPES = [
  ['addendum',   'Addendums'],
  ['bond',       'Bonds'],
  ['change_order', 'Change Orders'],
  ['contract',   'Contracts'],
  ['drawing',    'Drawings & Plans'],
  ['email',      'Emails'],   // saved from Correspondence (Ryan, Oct 10 2026)
  ['insurance_cert', 'Insurance Certs'],
  ['invoice',    'Invoices'],
  ['line_items', 'Line Items'],
  ['payroll',    'Payroll Information'],   // office + Executives only (the server leaves it out for others)
  ['permit',     'Permits'],
  ['photo',      'Photos'],
  ['purchase_order', 'Purchase Orders'],
  ['quote',      'Quotes'],
  ['rate_sheet', 'Rate Sheets'],
  ['rfi',        'RFIs'],
  ['spec',       'Specifications'],
  ['submittal',  'Submittals'],
  ['tm',         'T&M Billing'],
  ['traffic_control', 'Traffic Control Drawings'],   // printed from Schedule → Traffic Control (Ryan, Oct 30 2026)
  ['usa',        'USA Dig Tickets'],
  ['used_quote', 'Used Quote'],   // the quote the job was won on, saved at award (Ryan, Oct 22 2026)
  ['vendor_quote', 'Vendor Quotes'],
  ['other',      'Other'],
];
const typeLabel = t => (TYPES.find(([k]) => k === t) || [t, t])[1];
// Two views of the one ledger (Ryan, Oct 13 2026: "PMs see one, the office sees another, and
// a switch at the bottom to see the other — it's overwhelming, but I want all the documents in
// one home"). Each category belongs to the PM side, the office side, or both; a search always
// looks through everything.
const SIDES = {
  pm: { label: 'PM documents', icon: '📐', types: ['submittal', 'drawing', 'spec', 'addendum', 'quote', 'used_quote', 'vendor_quote', 'rfi', 'change_order', 'permit', 'usa', 'photo', 'email', 'other'] },
  office: { label: 'Office documents', icon: '🧾', types: ['contract', 'used_quote', 'line_items', 'invoice', 'purchase_order', 'vendor_quote', 'rate_sheet', 'tm', 'change_order', 'insurance_cert', 'bond', 'payroll', 'permit', 'email', 'other'] },
};
const inSide = (side, t) => side === 'all' || (SIDES[side] && SIDES[side].types.includes(t || 'other'));
const SIDE_KEY = 'titan_docs_side';
function defaultSide() {
  try { const v = localStorage.getItem(SIDE_KEY); if (v === 'pm' || v === 'office' || v === 'all') return v; } catch { /* default below */ }
  let role = '';
  try { const ss = JSON.parse(localStorage.getItem('titan_session') || 'null'); role = (ss && (ss.role || (ss.user && ss.user.role))) || ''; } catch { role = ''; }
  return ['Accounting', 'OfficeStaff'].includes(role) ? 'office' : ['Executive', 'Admin', 'admin'].includes(role) ? 'all' : 'pm';
}
const typeRank  = t => { const i = TYPES.findIndex(([k]) => k === t); return i < 0 ? TYPES.length : i; };
// Through the server so the tab and any Save carry the document's real title, not the
// random upload filename.
const fileUrl = d => `${API_BASE}/documents/${d.id}/file`;
const when = s => s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
const muted = { fontSize: 13, color: '#9ca3af' };

// ── Plans to Fieldwire (Pat, Sep 28 2026) ─────────────────────────────────────
// A strip at the top of the bid's ledger: which plans are up in Fieldwire (and how far
// Fieldwire has got splitting them), and a picker to send more. The first send makes
// the Fieldwire project; at award it takes the job number.
const isPlanFile = d => /\.(pdf|jpe?g|png|gif|webp)$/i.test(d.filename || d.original_name || '') || /^(application\/pdf|image\/)/i.test(d.mime_type || '');
const uploadLabel = u => {
  if (u.status === 'processed') return `✓ ${u.plans_created ? `${u.plans_created} sheet${u.plans_created === 1 ? '' : 's'}` : 'in Fieldwire'}`;
  if (u.status === 'errored') return `✗ ${u.error ? u.error : 'Fieldwire could not read it'}`;
  return `⏳ ${u.status || 'sending'}${u.pages_split ? ` · ${u.pages_split} page${u.pages_split === 1 ? '' : 's'}` : ''}`;
};
export function FieldwirePlans({ bidId, docs, canEdit }) {
  const [state, setState] = useState(null);
  const [picking, setPicking] = useState(false);
  const [chosen, setChosen] = useState({});
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');
  const [housekeeping, setHousekeeping] = useState('');
  const [hkMsg, setHkMsg] = useState('');
  const load = useCallback(() => apiFetch(`/bids/${bidId}/fieldwire`).then(d => setState(d || { configured: false })).catch(() => setState({ configured: false })), [bidId]);
  useEffect(() => { load(); }, [load]);
  // While Fieldwire is still splitting pages, or the sheets are being named, look again every few seconds.
  const namingRunning = !!(state?.naming && ['queued', 'running'].includes(state.naming.status));
  const busy = !!state?.uploads?.some(u => !['processed', 'errored'].includes(u.status)) || namingRunning;
  async function nameSheets() {
    setHousekeeping('name'); setErr(''); setHkMsg('');
    try { const r = await apiFetch(`/projects/${bidId}/fieldwire/name-sheets`, { method: 'POST', body: '{}' }); if (r.note) setHkMsg(r.note); await load(); }
    catch (e) { setErr(e?.error || e?.message || 'Could not start naming the sheets.'); }
    setHousekeeping('');
  }
  useEffect(() => { if (!busy) return undefined; const t = setInterval(load, 8000); return () => clearInterval(t); }, [busy, load]);
  if (!state || !state.configured) return null;
  const uploads = state.uploads || [];
  const up = new Set(uploads.filter(u => u.status !== 'errored').map(u => u.document_id));
  const plans = docs.filter(d => isPlanFile(d) && !up.has(d.id));
  const openPicker = () => { setChosen(Object.fromEntries(plans.filter(d => d.doc_type === 'drawing').map(d => [d.id, true]))); setErr(''); setPicking(true); };
  async function send() {
    const ids = Object.keys(chosen).filter(k => chosen[k]);
    if (!ids.length || sending) return;
    setSending(true); setErr('');
    try {
      const r = await apiFetch(`/bids/${bidId}/fieldwire/plans`, { method: 'POST', body: JSON.stringify({ document_ids: ids }) });
      setState(s => ({ ...(s || {}), configured: true, link: r.link, uploads: r.uploads || [] }));
      setPicking(false);
    } catch (e) { setErr(e?.error || e?.message || 'Could not send those to Fieldwire.'); }
    setSending(false);
  }
  const n = Object.values(chosen).filter(Boolean).length;
  // Housekeeping (Ryan, Sep 30 2026): take every plan off the project, or start the bid
  // over on a fresh project made from the Titan template (the old one stays in Fieldwire).
  const unreachable = !!state.link && state.reachable === false;
  async function removeAll() {
    if (!window.confirm(`Take every plan off "${state.link?.name}" in Fieldwire? Tasks pinned to those sheets lose their spot. The files stay in the app and can be sent again.`)) return;
    setHousekeeping('remove'); setErr(''); setHkMsg('');
    try { const r = await apiFetch(`/bids/${bidId}/fieldwire/plans/remove`, { method: 'POST', body: '{}' }); setHkMsg(`Removed ${r.deleted.length} plan${r.deleted.length === 1 ? '' : 's'}${r.failed.length ? ` — ${r.failed.length} could not be removed` : ''}.`); await load(); }
    catch (e) { setErr(e?.error || e?.message || 'Could not remove the plans.'); }
    setHousekeeping('');
  }
  async function startOver() {
    const tpl = state.template ? state.template.name : 'the account template';
    if (!window.confirm(`Start over? This makes a NEW Fieldwire project for this bid from ${tpl}, links the bid to it, and clears the plan uploads and any saved AI layout here. The old project (${state.link?.name || 'none'}) stays in Fieldwire untouched — delete or archive it there if you want.`)) return;
    setHousekeeping('start'); setErr(''); setHkMsg('');
    try { const r = await apiFetch(`/bids/${bidId}/fieldwire/start-over`, { method: 'POST', body: '{}' }); setHkMsg(`New project "${r.link?.name}"${r.template ? ` from the ${r.template.name}` : ''}${r.crew?.added?.length ? ` · ${r.crew.added.length} people invited` : ''}. Send the plans again below.`); await load(); window.dispatchEvent(new CustomEvent('titan:fieldwire-changed', { detail: { bidId } })); }
    catch (e) { setErr(e?.error || e?.message || 'Could not start over.'); }
    setHousekeeping('');
  }
  return (
    <div style={{ border: '1.5px solid #ccfbf1', background: '#f0fdfa', borderRadius: 10, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: '#0f766e' }}>📐 Fieldwire</span>
        {state.link
          ? <span style={{ fontSize: 12.5, color: '#111827', fontWeight: 700, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{state.link.name}</span>
          : <span style={{ fontSize: 12.5, color: '#6b7280', flex: 1 }}>No Fieldwire project yet — the first send makes one{state.template ? ` from the ${state.template.name}` : ''}.</span>}
        {state.link && <a href={state.link.openUrl} target="_blank" rel="noreferrer" style={{ padding: '6px 11px', borderRadius: 999, border: '1px solid #99f6e4', background: '#fff', color: '#0f766e', fontSize: 12, fontWeight: 800, textDecoration: 'none', whiteSpace: 'nowrap' }}>Open in Fieldwire ↗</a>}
        {canEdit && state.link && !picking && (
          <>
            {!unreachable && <button onClick={removeAll} disabled={!!housekeeping} title="Delete every plan sheet on this Fieldwire project" style={{ padding: '6px 11px', borderRadius: 999, border: '1px solid #fca5a5', background: '#fff', color: '#b91c1c', fontSize: 12, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>{housekeeping === 'remove' ? 'Removing…' : '🗑 Remove all plans'}</button>}
            <button onClick={startOver} disabled={!!housekeeping} title={state.template ? `Make a new Fieldwire project from the ${state.template.name} and link this bid to it` : 'Make a new Fieldwire project and link this bid to it'} style={{ padding: '6px 11px', borderRadius: 999, border: '1px solid #99f6e4', background: '#fff', color: '#0f766e', fontSize: 12, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>{housekeeping === 'start' ? 'Making it…' : `↺ Start over${state.template ? ' from the ' + state.template.name : ''}`}</button>
          </>
        )}
        {canEdit && !picking && plans.length > 0 && (
          <button onClick={openPicker} style={{ padding: '6px 11px', borderRadius: 999, border: '1px solid #99f6e4', background: '#fff', color: '#0f766e', fontSize: 12, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {uploads.length ? '＋ Send more plans' : '⇧ Send plans to Fieldwire'}
          </button>
        )}
      </div>
      {unreachable && <div style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '6px 10px' }}>The app can’t reach this project — it lives in another Fieldwire account (the old test login). Its plans can’t be removed from here. <b>Start over</b> makes a fresh project in your account{state.template ? ` from the ${state.template.name}` : ''}.</div>}
      {hkMsg && <div style={{ fontSize: 12.5, color: '#15803d', fontWeight: 700 }}>{hkMsg}</div>}
      {/* The "name your plans" step, done by the app: runs on its own when an upload finishes. */}
      {state.link && (namingRunning || state.naming || state.unnamed > 0) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5 }}>
          {namingRunning ? (
            <span style={{ color: '#5b21b6', fontWeight: 700 }}>🔤 Naming the sheets from their title blocks… {state.naming.progress?.done || 0} of {state.naming.progress?.total || '?'}</span>
          ) : state.naming && state.naming.status === 'done' && state.naming.result ? (
            <span style={{ color: state.naming.result.named ? '#15803d' : '#92400e', fontWeight: 700 }}>🔤 {state.naming.result.named} sheet{state.naming.result.named === 1 ? '' : 's'} named{state.naming.result.no_picture ? ` · ${state.naming.result.no_picture} had no picture yet` : ''}{state.naming.result.unreadable ? ` · ${state.naming.result.unreadable} number${state.naming.result.unreadable === 1 ? '' : 's'} not readable` : ''}{state.unnamed > 0 ? ` · ${state.unnamed} still by page number` : ''}</span>
          ) : state.naming && state.naming.status === 'failed' ? (
            <span style={{ color: '#b91c1c', fontWeight: 700 }}>Naming failed — {state.naming.error}</span>
          ) : state.unnamed > 0 ? (
            <span style={{ color: '#92400e', fontWeight: 700 }}>{state.unnamed} sheet{state.unnamed === 1 ? '' : 's'} still named by page number</span>
          ) : null}
          {canEdit && !namingRunning && state.unnamed > 0 && (
            <button onClick={nameSheets} disabled={!!housekeeping} title="Read each sheet's title block and set its sheet number and title in Fieldwire" style={{ padding: '4px 10px', borderRadius: 999, border: '1px solid #ddd6fe', background: '#fff', color: '#5b21b6', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>{housekeeping === 'name' ? 'Starting…' : '🔤 Name the sheets'}</button>
          )}
        </div>
      )}
      {err && !picking && <div style={{ fontSize: 12.5, color: '#b91c1c' }}>{err}</div>}
      {uploads.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {uploads.map(u => (
            <div key={u.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12.5 }}>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#111827', fontWeight: 600 }}>{u.title}</span>
              <span style={{ color: u.status === 'errored' ? '#b91c1c' : u.status === 'processed' ? '#15803d' : '#92400e', whiteSpace: 'nowrap' }}>{uploadLabel(u)}</span>
            </div>
          ))}
        </div>
      )}
      {picking && (
        <div style={{ borderTop: '1px dashed #99f6e4', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 11.5, color: '#0f766e', fontWeight: 700 }}>Which files are plan sheets? A multi-page PDF becomes one sheet per page.</div>
          {plans.map(d => (
            <label key={d.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!chosen[d.id]} onChange={e => setChosen(c => ({ ...c, [d.id]: e.target.checked }))} />
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title}</span>
              <span style={{ fontSize: 11, color: '#9ca3af' }}>{typeLabel(d.doc_type)}</span>
            </label>
          ))}
          {err && <div style={{ fontSize: 12.5, color: '#b91c1c' }}>{err}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={send} disabled={!n || sending} style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: n && !sending ? '#0f766e' : '#a7f3d0', color: '#fff', fontSize: 12.5, fontWeight: 800, cursor: n && !sending ? 'pointer' : 'default' }}>
              {sending ? 'Sending…' : `Send ${n || ''} to Fieldwire`}
            </button>
            <button onClick={() => setPicking(false)} disabled={sending} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── 📋 Paste into the Documents tile (Ryan, Oct 3 2026) ───────────────────────
// Phones copy photos and text from messages all day; the tile only took files. "Paste
// what I copied" asks the browser for the clipboard (the phone shows its own Paste
// bubble — tap it); where that isn't allowed, the box below takes a long-press → Paste.
// Pictures are filed as photos; text becomes a .txt document named from its first line.
const PASTE_TYPES = [['photo', 'Photo'], ['other', 'Other'], ['drawing', 'Drawings & Plans'], ['spec', 'Specs'], ['addendum', 'Addendum'], ['quote', 'Quote'], ['contract', 'Contract'], ['vendor_quote', 'Vendor quote'], ['rfi', 'RFI'], ['change_order', 'Change order'], ['permit', 'Permit']];
const stamp = () => new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).replace(',', '').replace(/:/g, '.').replace(/\//g, '-').replace(/\s+/g, ' ');   // "Oct 3 9.39 PM" — safe in a file name
function PasteToDocs({ onSave, onClose }) {
  const [images, setImages] = useState([]);   // File[]
  const [text, setText] = useState('');
  const [title, setTitle] = useState('');
  const [docType, setDocType] = useState('other');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const take = (files, txt) => {
    const imgs = Array.from(files || []).filter(f => /^image\//.test(f.type || ''));
    if (imgs.length) { setImages(xs => [...xs, ...imgs.map((f, i) => (f.name && f.name !== 'image.png' ? f : new File([f], `Pasted photo ${stamp()}${imgs.length > 1 ? ' ' + (i + 1) : ''}.${(f.type.split('/')[1] || 'png').replace('jpeg', 'jpg')}`, { type: f.type })))]); setDocType('photo'); }
    if (txt && txt.trim()) { setText(t => (t ? t + '\n' : '') + txt); if (!title) setTitle(txt.trim().split('\n')[0].slice(0, 60)); }
    if (!imgs.length && !(txt && txt.trim())) setMsg('Nothing came through — long-press in the box below and choose Paste.');
    else setMsg('');
  };
  async function readClipboard() {
    setMsg('');
    try {
      if (navigator.clipboard && navigator.clipboard.read) {
        const items = await navigator.clipboard.read();
        const files = []; let txt = '';
        for (const it of items) {
          const imgType = it.types.find(t => /^image\//.test(t));
          if (imgType) { const b = await it.getType(imgType); files.push(new File([b], 'image.png', { type: imgType })); }
          else if (it.types.includes('text/plain')) { txt += await (await it.getType('text/plain')).text(); }
        }
        take(files, txt); return;
      }
      if (navigator.clipboard && navigator.clipboard.readText) { take([], await navigator.clipboard.readText()); return; }
      setMsg('This browser won’t hand the clipboard over — long-press in the box below and choose Paste.');
    } catch { setMsg('The phone didn’t allow it — long-press in the box below and choose Paste.'); }
  }
  async function save() {
    if (busy) return;
    const files = [...images]; const titles = images.map(f => f.name.replace(/\.[a-z0-9]+$/i, ''));
    if (text.trim()) {
      const name = (title.trim() || `Pasted note ${stamp()}`).replace(/[\\/:*?"<>|]/g, ' ').slice(0, 80);
      files.push(new File([text], `${name}.txt`, { type: 'text/plain' })); titles.push(name);
    }
    if (!files.length) { setMsg('Paste something first.'); return; }
    setBusy(true);
    const ok = await onSave(files, { docType: text.trim() && !images.length && docType === 'photo' ? 'other' : docType, titles });
    setBusy(false);
    if (ok === files.length) { setImages([]); setText(''); setTitle(''); setMsg(`Filed ${ok} — it’s in the list above.`); setTimeout(onClose, 1500); }
    else setMsg(ok ? `Filed ${ok} of ${files.length}; the rest did not go — try again.` : 'It did not go — check the connection and try again.');
  }
  const lblS = { fontSize: 10.5, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 4 };
  const inpS = { border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 10px', fontSize: 14, width: '100%', boxSizing: 'border-box', background: '#fff', fontFamily: 'inherit' };
  return (
    <div style={{ border: '1.5px solid #c7d2fe', background: '#f8faff', borderRadius: 12, padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13.5, fontWeight: 800, color: '#3730a3', flex: 1 }}>📋 File something you copied</span>
        <button onClick={readClipboard} style={{ padding: '10px 14px', borderRadius: 8, border: 'none', background: '#4338ca', color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>Paste what I copied</button>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: '#9ca3af', cursor: 'pointer', lineHeight: 1 }}>×</button>
      </div>
      {/* The fallback that always works on a phone: long-press here → Paste. */}
      <textarea value={text} onChange={e => setText(e.target.value)} rows={text ? 5 : 2}
        onPaste={e => { const cd = e.clipboardData; const files = cd && cd.files && cd.files.length ? cd.files : null; if (files) { e.preventDefault(); take(files, cd.getData('text/plain')); } else if (!title) { const t = cd ? cd.getData('text/plain') : ''; if (t) setTitle(t.trim().split('\n')[0].slice(0, 60)); } }}
        placeholder="…or long-press here and choose Paste (works for text and photos)" style={{ ...inpS, resize: 'vertical' }} />
      {images.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {images.map((f, i) => (
            <div key={i} style={{ position: 'relative' }}>
              <img src={URL.createObjectURL(f)} alt={f.name} style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb' }} />
              <button onClick={() => setImages(xs => xs.filter((_, j) => j !== i))} title="Leave this one out" style={{ position: 'absolute', top: -6, right: -6, width: 22, height: 22, borderRadius: 11, border: 'none', background: '#111827', color: '#fff', fontSize: 13, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
          ))}
        </div>
      )}
      {(text.trim() || images.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
          {text.trim() && <div><label style={lblS}>Name for the note</label><input value={title} onChange={e => setTitle(e.target.value)} placeholder={`Pasted note ${stamp()}`} style={inpS} /></div>}
          <div><label style={lblS}>File it under</label><select value={docType} onChange={e => setDocType(e.target.value)} style={inpS}>{PASTE_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
        </div>
      )}
      {msg && <div style={{ fontSize: 13, fontWeight: 700, color: /^Filed \d+ —/.test(msg) ? '#15803d' : '#92400e' }}>{msg}</div>}
      {(text.trim() || images.length > 0) && (
        <button onClick={save} disabled={busy} style={{ padding: '12px', borderRadius: 10, border: 'none', background: '#1e3a5f', color: '#fff', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
          {busy ? 'Filing…' : `File ${images.length + (text.trim() ? 1 : 0)} in Documents`}
        </button>
      )}
    </div>
  );
}

// `manage` — when given, decides whether the remove buttons and the upload footer show
// (the bid page's tile reads first and turns these on with ✎ Edit). Left out, they follow
// canEdit as before (the job page's pillow).
// fieldwireId: what the Fieldwire plans strip works off — the bid by default; a job with no bid
// passes its project id here (Ryan, Oct 3 2026) while the ledger still loads by project.
// noFieldwire: leave the send-plans-to-Fieldwire strip off (the old-system window has its own Fieldwire step).
export default function DocumentsPillow({ bidId, projectId, canEdit = false, compact = false, manage, fieldwireId = null, noFieldwire = false }) {
  const managing = canEdit && (manage === undefined ? true : !!manage);
  const [docs, setDocs] = useState(null);
  const [uType, setUType] = useState('drawing');
  const [openKeys, setOpenKeys] = useState({});
  // A big job's ledger runs to dozens of files (2631, Sep 2026): each category folds
  // to one line with a count, and a search finds a file without opening any of them.
  // Searching opens every category that has a hit.
  const [q, setQ] = useState('');
  const [openTypes, setOpenTypes] = useState(null);   // null = nothing chosen yet (small lists open, big ones folded)
  const [side, setSideState] = useState(defaultSide);
  const setSide = v => { setSideState(v); setOpenTypes(null); try { localStorage.setItem(SIDE_KEY, v); } catch { /* this device only */ } };
  const [sideNote, setSideNote] = useState('');
  const base = bidId ? `/bids/${bidId}/documents` : projectId ? `/projects/${projectId}/documents` : null;

  const load = useCallback(() => {
    if (!base) return;
    apiFetch(base).then(d => setDocs(Array.isArray(d) ? d : [])).catch(() => setDocs([]));
  }, [base]);
  useEffect(() => { load(); }, [load]);
  // A print elsewhere on the page (quote, submittal package) files a document; it
  // announces itself so this list picks it up without a reload.
  useEffect(() => {
    window.addEventListener('titan:documents-changed', load);
    return () => window.removeEventListener('titan:documents-changed', load);
  }, [load]);

  async function uploadFiles(files, { docType = null, titles = null } = {}) {
    let ok = 0; const refused = [];
    for (const [i, f] of Array.from(files).entries()) {
      const fd = new FormData();
      fd.append('file', f);
      if (bidId) fd.append('bid_id', bidId);
      if (projectId) fd.append('project_id', projectId);
      fd.append('doc_type', docType || uType);
      fd.append('title', (titles && titles[i]) || f.name);
      const r = await fetch(`${API_BASE}/documents`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd }).catch(() => null);
      if (r && r.ok) ok++;
      else { const j = r ? await r.json().catch(() => ({})) : {}; refused.push(`${f.name}: ${j.error || (r ? `upload failed (${r.status})` : 'no connection')}`); }
    }
    load();
    const t = docType || uType;
    setSideNote(refused.length ? `Not uploaded — ${refused.join('; ')}` : ok && !inSide(side, t) ? `Filed under ${typeLabel(t)} — that's in ${SIDES[side === 'pm' ? 'office' : 'pm'].label}.` : '');
    return ok;
  }
  const [pasteOpen, setPasteOpen] = useState(false);
  async function remove(id) {
    if (!window.confirm('Remove this document from the ledger?')) return;
    await apiFetch(`/documents/${id}`, { method: 'DELETE' }).catch(() => {});
    load();
  }
  // Rename, or move to another category, right on the row (Ryan, Oct 1 2026: four
  // photos on the 6245 bid went in as Drawings and there was no way to fix it).
  const [renaming, setRenaming] = useState(null);   // { id, value }
  const [err, setErr] = useState('');
  async function patchDoc(id, body) {
    setErr('');
    try { await apiFetch(`/documents/${id}`, { method: 'PATCH', body: JSON.stringify(body) }); load(); }
    catch (e) { setErr(e?.error || 'Could not change that.'); }
  }
  function commitRename() {
    const r = renaming; setRenaming(null);
    if (!r) return;
    const v = r.value.trim();
    if (v && v !== r.was) patchDoc(r.id, { title: v });
  }

  if (!base) return <div style={muted}>No job to show documents for.</div>;
  if (docs === null) return <div style={muted}>Loading…</div>;

  // Group by type, then by doc_key — the revisions of one document sit together, newest
  // first (the server orders them that way).
  const ql0 = q.trim();
  const shown = ql0 ? docs : docs.filter(d => inSide(side, d.doc_type));
  const sideCount = sd => new Set(docs.filter(d => inSide(sd, d.doc_type)).map(d => d.doc_key || d.id)).size;
  const byType = {};
  for (const d of shown) {
    const t = d.doc_type || 'other';
    const k = d.doc_key || d.id;
    if (!byType[t]) byType[t] = {};
    if (!byType[t][k]) byType[t][k] = [];
    byType[t][k].push(d);
  }
  const types = Object.keys(byType).sort((a, b) => typeRank(a) - typeRank(b));
  const ql = q.trim().toLowerCase();
  const hit = d => !ql || `${d.title || ''} ${d.original_name || ''} ${d.where || ''} ${d.created_by_name || ''}`.toLowerCase().includes(ql);
  const countIn = t => Object.keys(byType[t]).length;
  // Folded by default once the ledger is big; a short list stays open as before.
  const big = shown.length > 10;   // Pat: open until there are more than ten
  const isOpen = t => (ql ? Object.values(byType[t]).some(revs => revs.some(hit)) : openTypes ? !!openTypes[t] : !big);
  const toggle = t => setOpenTypes(o => ({ ...(o || Object.fromEntries(types.map(x => [x, !big]))), [t]: !isOpen(t) }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {!noFieldwire && (fieldwireId || bidId) && <FieldwirePlans bidId={fieldwireId || bidId} docs={docs} canEdit={canEdit} />}
      {docs.length > 6 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={`Search ${docs.length} files…`}
            style={{ flex: 1, border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 11px', fontSize: 13, background: '#fff' }} />
          {ql && <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{types.reduce((n, t) => n + Object.values(byType[t]).filter(revs => revs.some(hit)).length, 0)} found</span>}
          {big && !ql && (
            <button onClick={() => setOpenTypes(Object.fromEntries(types.map(t => [t, !types.every(isOpen)])))}
              style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 7, padding: '6px 10px', fontSize: 12, fontWeight: 700, color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {types.every(isOpen) ? 'Fold all' : 'Open all'}
            </button>
          )}
        </div>
      )}
      {err && <div style={{ fontSize: 12.5, color: '#b91c1c', fontWeight: 600 }}>{err}</div>}
      {docs.length > 0 && shown.length === 0 && (
        <div style={muted}>Nothing in {SIDES[side] ? SIDES[side].label : 'this view'} yet — the switch below shows the rest.</div>
      )}
      {docs.length === 0 && (
        <div style={muted}>Nothing on file yet. Printed quotes and packages land here on their own, and files added on the Plans tile or a submittal show up here too{managing && !compact ? '; drop a file below to add one' : canEdit && !compact ? '; press ✎ Edit to add one' : ''}.</div>
      )}

      {types.map(t => {
        const groups = Object.entries(byType[t]).filter(([, revs]) => revs.some(hit));
        if (ql && !groups.length) return null;
        const open = isOpen(t);
        return (
        <div key={t}>
          <button onClick={() => toggle(t)} title={open ? 'Fold this category' : 'Open this category'}
            style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', background: 'none', border: 'none', padding: '2px 0', marginBottom: 6, cursor: 'pointer', textAlign: 'left' }}>
            <span style={{ fontSize: 10, color: '#9ca3af', width: 10 }}>{open ? '▾' : '▸'}</span>
            <span style={{ fontSize: 10.5, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>{typeLabel(t)}</span>
            <span style={{ fontSize: 10.5, fontWeight: 700, color: '#9ca3af', background: '#f3f4f6', borderRadius: 10, padding: '0 7px' }}>{countIn(t)}</span>
          </button>
          {open && <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {groups.map(([k, revs]) => {
              const latest = revs[0];
              const older = revs.slice(1);
              const open = !!openKeys[k];
              return (
                <div key={k} style={{ border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: '8px 10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <span style={{ fontSize: 16 }}>📄</span>
                    {renaming && renaming.id === latest.id ? (
                      <input autoFocus value={renaming.value} onChange={e => setRenaming(r => ({ ...r, value: e.target.value }))}
                        onBlur={commitRename} onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenaming(null); }}
                        style={{ flex: 1, minWidth: 0, border: '1px solid #93c5fd', borderRadius: 6, padding: '4px 8px', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' }} />
                    ) : (
                    <a href={fileUrl(latest)} target="_blank" rel="noreferrer"
                      style={{ flex: 1, minWidth: 0, color: '#1d4ed8', fontWeight: 700, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {latest.title}
                    </a>
                    )}
                    {latest.revision > 1 && (
                      <span style={{ fontSize: 10, fontWeight: 800, color: '#5b21b6', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 10, padding: '1px 7px', whiteSpace: 'nowrap' }}>rev {latest.revision}</span>
                    )}
                    {!!latest.generated && <span style={{ fontSize: 10, fontWeight: 700, color: '#15803d', whiteSpace: 'nowrap' }}>printed</span>}
                    {/* A file that lives on another tile (Plans, a submittal item) — shown
                        here, managed there. */}
                    {!!latest.readonly && latest.where && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 10, padding: '1px 7px', whiteSpace: 'nowrap' }}>{latest.where}</span>
                    )}
                    <span style={{ fontSize: 11.5, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                      {when(latest.created_at)}{latest.created_by_name ? ` · ${latest.created_by_name}` : ''}
                    </span>
                    {/* Rename on any row the office may manage — a Plans drawing or a submittal's
                        file too (it is renamed where it lives). Re-filing is for ledger rows and
                        Plans drawings; a quote's or a submittal's file stays with its owner. */}
                    {managing && (!latest.readonly || latest.can_rename) && (
                      <button onClick={() => setRenaming({ id: latest.id, value: latest.title || '', was: latest.title || '' })} title="Rename"
                        style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 13, cursor: 'pointer', lineHeight: 1, padding: '0 2px' }}>✎</button>
                    )}
                    {managing && (!latest.readonly || latest.can_refile) && (
                      <select value={latest.doc_type || 'other'} title={latest.readonly ? 'Move it off the Plans tile into another category' : 'Move to another category'} onChange={e => patchDoc(latest.id, { doc_type: e.target.value })}
                        style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '2px 4px', fontSize: 11, color: '#6b7280', background: '#fff', maxWidth: 120, cursor: 'pointer' }}>
                        {TYPES.filter(([k]) => !latest.readonly || k !== 'vendor_quote').map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                      </select>
                    )}
                    {managing && !latest.readonly && (
                      <button onClick={() => remove(latest.id)} title="Remove"
                        style={{ background: 'none', border: 'none', color: '#c9ccd1', fontSize: 16, cursor: 'pointer', lineHeight: 1 }}>×</button>
                    )}
                  </div>
                  {older.length > 0 && (
                    <div style={{ marginTop: 6 }}>
                      <button onClick={() => setOpenKeys(o => ({ ...o, [k]: !open }))}
                        style={{ background: 'none', border: 'none', padding: 0, color: '#6b7280', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>
                        {open ? 'Hide' : 'Show'} {older.length} earlier revision{older.length !== 1 ? 's' : ''}
                      </button>
                      {open && older.map(d => (
                        <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginTop: 4, paddingLeft: 24 }}>
                          <a href={fileUrl(d)} target="_blank" rel="noreferrer"
                            style={{ flex: 1, color: '#6b7280', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            rev {d.revision} · {when(d.created_at)}{d.created_by_name ? ` · ${d.created_by_name}` : ''}
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>}
        </div>
        );
      })}

      {docs.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', borderTop: '1px solid #eef0f2', paddingTop: 10 }}>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: '#6b7280' }}>{ql ? 'Searching all documents' : 'Show'}</span>
          <div style={{ display: 'inline-flex', background: '#f3f4f6', borderRadius: 999, padding: 3, gap: 2, flexWrap: 'wrap' }}>
            {[['pm', `${SIDES.pm.icon} PM`], ['office', `${SIDES.office.icon} Office`], ['all', 'All']].map(([k, l]) => (
              <button key={k} onClick={() => setSide(k)} title={k === 'all' ? 'Every document on the job' : SIDES[k].label}
                style={{ padding: '6px 12px', borderRadius: 999, border: 'none', background: side === k ? '#1e3a5f' : 'transparent', color: side === k ? '#fff' : '#374151', fontSize: 12.5, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {l} <span style={{ opacity: 0.7, fontWeight: 700 }}>{k === 'all' ? new Set(docs.map(d => d.doc_key || d.id)).size : sideCount(k)}</span>
              </button>
            ))}
          </div>
          {sideNote && <span style={{ fontSize: 12, color: '#92400e', fontWeight: 700 }}>{sideNote}</span>}
        </div>
      )}
      {managing && !compact && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', borderTop: '1px dashed #eef0f2', paddingTop: 10 }}>
          <select value={uType} onChange={e => setUType(e.target.value)}
            style={{ border: '1px solid #d1d5db', borderRadius: 7, padding: '7px 9px', fontSize: 13, background: '#fff' }}>
            {TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <AttachButton multiple accept="any" icon="📄" label="Drag & drop a file here, or click to file it"
            style={{ flex: '1 1 260px', padding: '12px 12px', fontSize: 13 }} onFiles={fs => uploadFiles(fs)} />
          <button onClick={() => setPasteOpen(o => !o)} title="File something you copied — a photo, a screenshot or text from a message"
            style={{ padding: '11px 14px', borderRadius: 8, border: '1.5px solid #c7d2fe', background: pasteOpen ? '#4338ca' : '#eef2ff', color: pasteOpen ? '#fff' : '#4338ca', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>📋 Paste</button>
        </div>
      )}
      {managing && !compact && pasteOpen && <PasteToDocs onSave={uploadFiles} onClose={() => setPasteOpen(false)} />}
    </div>
  );
}
